import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../components/ui/sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, MoreVertical, Pencil, Copy, Download, Grid3x3, MessageSquare, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PeriodDialog } from '../components/availability/PeriodDialog';
import { SubmissionReview } from '../components/availability/SubmissionReview';
import { SUB_STATUS_STYLES, fmtShortDate, fmtDateTime } from '../components/availability/constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIOD_STATUS_COLORS = {
  open: 'default', closed: 'secondary', locked: 'secondary', archived: 'outline',
};

export default function AvailabilityAdmin() {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // create | edit | duplicate
  const [dialogInitial, setDialogInitial] = useState(null);
  const [reviewMember, setReviewMember] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  const fetchPeriods = useCallback(async (keepSelection = true) => {
    try {
      const res = await axios.get(`${API_URL}/api/availability/periods?include_archived=${includeArchived}`);
      setPeriods(res.data);
      setPeriodId((prev) => (keepSelection && prev && res.data.some((p) => p.id === prev)) ? prev : (res.data[0]?.id || null));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);

  const fetchOverview = useCallback(async () => {
    if (!periodId) { setOverview(null); return; }
    try {
      const res = await axios.get(`${API_URL}/api/availability/periods/${periodId}/overview`);
      setOverview(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [periodId]);

  useEffect(() => { fetchPeriods(); }, [fetchPeriods]);
  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const selectedPeriod = periods.find((p) => p.id === periodId);

  const openDialog = (mode, initial = null) => {
    setDialogMode(mode);
    setDialogInitial(initial);
    setDialogOpen(true);
  };

  const handlePeriodSave = async (form) => {
    try {
      if (dialogMode === 'edit') {
        await axios.put(`${API_URL}/api/availability/periods/${dialogInitial.id}`, form);
        toast.success('Period updated');
      } else if (dialogMode === 'duplicate') {
        await axios.post(`${API_URL}/api/availability/periods/${dialogInitial.id}/duplicate`, form);
        toast.success('Period duplicated');
      } else {
        await axios.post(`${API_URL}/api/availability/periods`, form);
        toast.success('Period created — staff can now submit availability');
      }
      setDialogOpen(false);
      fetchPeriods();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const setStatus = async (status) => {
    try {
      await axios.post(`${API_URL}/api/availability/periods/${periodId}/status`, { status });
      toast.success(`Period ${status}`);
      fetchPeriods();
      fetchOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleExport = (mode) => {
    const token = JSON.parse(localStorage.getItem('currentUser') || '{}')?.token;
    fetch(`${API_URL}/api/availability/periods/${periodId}/export?mode=${mode}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => r.text()).then((csv) => {
      const blob = new Blob([csv], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `availability_${mode}_${selectedPeriod?.name?.replace(/\s+/g, '_') || 'export'}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    }).catch(() => toast.error('Export failed'));
  };

  const rows = (overview?.rows || []).filter((r) => {
    if (statusFilter !== 'all') {
      if (statusFilter === 'not_submitted' ? r.status === 'submitted' : r.status !== statusFilter) return false;
    }
    if (typeFilter !== 'all' && r.staff_type !== typeFilter) return false;
    if (search && !r.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const overdueCount = (overview?.rows || []).filter((r) => r.status === 'past_due').length;

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="availability-admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Availability Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Create scheduling periods and review staff submissions.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/availability-heatmap')} data-testid="goto-heatmap-button">
            <Grid3x3 className="h-4 w-4 mr-1.5" /> Heatmap
          </Button>
          <Button size="sm" onClick={() => openDialog('create')} data-testid="create-period-button">
            <Plus className="h-4 w-4 mr-1.5" /> New Period
          </Button>
        </div>
      </div>

      {/* Period selector + controls */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={periodId || ''} onValueChange={setPeriodId}>
            <SelectTrigger className="w-full sm:w-[300px] bg-background" data-testid="admin-period-select">
              <SelectValue placeholder="Select a period" />
            </SelectTrigger>
            <SelectContent className="bg-background">
              {periods.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name} ({p.status})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedPeriod && (
            <>
              <Badge variant={PERIOD_STATUS_COLORS[selectedPeriod.status] || 'outline'} data-testid="period-status-badge">
                {selectedPeriod.status.toUpperCase()}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {fmtShortDate(selectedPeriod.start_date)} – {fmtShortDate(selectedPeriod.end_date)} · Deadline {fmtShortDate(selectedPeriod.submission_deadline)}
              </span>
              {overdueCount > 0 && (
                <span className="flex items-center gap-1 text-xs font-semibold text-[#912F2F]" data-testid="overdue-indicator">
                  <AlertTriangle className="h-3.5 w-3.5" /> {overdueCount} past due
                </span>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="period-actions-menu">
                    <MoreVertical className="h-4 w-4 mr-1" /> Period Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-background">
                  <DropdownMenuItem onClick={() => openDialog('edit', selectedPeriod)} data-testid="period-edit">
                    <Pencil className="h-4 w-4 mr-2" /> Edit Period
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openDialog('duplicate', { ...selectedPeriod, name: `${selectedPeriod.name} (copy)` })} data-testid="period-duplicate">
                    <Copy className="h-4 w-4 mr-2" /> Duplicate
                  </DropdownMenuItem>
                  {selectedPeriod.status !== 'open' && <DropdownMenuItem onClick={() => setStatus('open')} data-testid="period-open">Open / Reopen</DropdownMenuItem>}
                  {selectedPeriod.status === 'open' && <DropdownMenuItem onClick={() => setStatus('closed')} data-testid="period-close">Close</DropdownMenuItem>}
                  {selectedPeriod.status !== 'locked' && <DropdownMenuItem onClick={() => setStatus('locked')} data-testid="period-lock">Lock</DropdownMenuItem>}
                  {selectedPeriod.status !== 'archived' && <DropdownMenuItem onClick={() => setStatus('archived')} className="text-red-600" data-testid="period-archive">Archive</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="export-menu">
                    <Download className="h-4 w-4 mr-1" /> Export CSV
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-background">
                  <DropdownMenuItem onClick={() => handleExport('summary')} data-testid="export-summary">Submission summary</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('detail')} data-testid="export-detail">Daily / shift detail</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto cursor-pointer">
            <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} className="rounded" data-testid="include-archived-toggle" />
            Show archived
          </label>
        </div>
      </Card>

      {/* Filters */}
      {overview && (
        <Card className="p-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input placeholder="Search employee…" value={search} onChange={(e) => setSearch(e.target.value)} className="sm:max-w-[220px]" data-testid="employee-search" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="sm:w-[180px] bg-background" data-testid="status-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-background">
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="not_submitted">Not submitted</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="past_due">Past due</SelectItem>
                <SelectItem value="reopened">Reopened</SelectItem>
                <SelectItem value="locked">Locked</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="sm:w-[140px] bg-background" data-testid="type-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-background">
                <SelectItem value="all">FT + PRN</SelectItem>
                <SelectItem value="ft">Full-time</SelectItem>
                <SelectItem value="prn">PRN</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>
      )}

      {/* Submissions table */}
      {overview && (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Completion</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-center">Preferred</TableHead>
                <TableHead className="text-center">Available</TableHead>
                <TableHead className="text-center">Unavail. days</TableHead>
                <TableHead className="text-center">Notes</TableHead>
                <TableHead>Last updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const meta = SUB_STATUS_STYLES[r.status] || SUB_STATUS_STYLES.not_started;
                return (
                  <TableRow
                    key={r.member_id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setReviewMember(r.member_id)}
                    data-testid={`submission-row-${r.member_id}`}
                  >
                    <TableCell>
                      <div className="font-medium">{r.name}</div>
                      <div className="text-xs text-muted-foreground">{r.position}</div>
                    </TableCell>
                    <TableCell><Badge variant="outline">{r.staff_type.toUpperCase()}</Badge></TableCell>
                    <TableCell><Badge variant={meta.variant}>{meta.label}</Badge></TableCell>
                    <TableCell className="min-w-[110px]">
                      <div className="flex items-center gap-2">
                        <Progress value={r.completion_pct} className="h-1.5 w-16" />
                        <span className="text-xs">{r.completion_pct}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">{r.submitted_at ? fmtDateTime(r.submitted_at) : '—'}</TableCell>
                    <TableCell className="text-center font-semibold text-[#1D9E75]">{r.preferred_count}</TableCell>
                    <TableCell className="text-center font-semibold text-[#3B82C4]">{r.available_count}</TableCell>
                    <TableCell className="text-center font-semibold text-[#D25454]">{r.unavailable_days}</TableCell>
                    <TableCell className="text-center">
                      {(r.has_comments || r.has_admin_notes) && <MessageSquare className="h-3.5 w-3.5 mx-auto text-muted-foreground" />}
                    </TableCell>
                    <TableCell className="text-xs">{r.last_saved_at ? fmtDateTime(r.last_saved_at) : '—'}</TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center text-sm text-muted-foreground py-8">No employees match the current filters</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {!overview && periods.length === 0 && (
        <Card className="p-10 text-center">
          <h3 className="font-semibold mb-1">No availability periods yet</h3>
          <p className="text-sm text-muted-foreground">Create your first scheduling period to start collecting staff availability.</p>
        </Card>
      )}

      <PeriodDialog
        open={dialogOpen} onOpenChange={setDialogOpen}
        initial={dialogInitial}
        title={dialogMode === 'edit' ? 'Edit Period' : dialogMode === 'duplicate' ? 'Duplicate Period' : 'New Availability Period'}
        onSave={handlePeriodSave}
      />
      <SubmissionReview
        open={!!reviewMember} onOpenChange={(o) => !o && setReviewMember(null)}
        periodId={periodId} memberId={reviewMember} onChanged={fetchOverview}
      />
    </div>
  );
}
